import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { breakpoints, colors, radius } from "@obs/tokens/index.stylex.ts";
import type { StyleXStyles } from "@stylexjs/stylex";
import * as stylex from "@stylexjs/stylex";

import { Wrapper } from "./Wrapper";

export type WrapperClickableProps = {
	disabled?: boolean;
	loading?: boolean;
	selected?: boolean;
	sx?: StyleXStyles;
} & useRender.ComponentProps<"button">;

const styles = stylex.create({
	root: {
		display: "flex",
		padding: 0,
		border: "none",
		paddingInline: 0,
		paddingBlock: 0,
		borderWidth: 0,
		cursor: "pointer",
		WebkitTapHighlightColor: "transparent",
		margin: 0,
		backgroundColor: "inherit",
		textDecoration: "none",
		inlineSize: "100%",
	},
	wrapper: {
		inlineSize: "100%",
		position: "relative",
		cursor: "pointer",
		borderRadius: {
			default: 0,
			[breakpoints.md]: radius.default,
		},
	},
	wrapperHover: {
		backgroundColor: {
			default: null,
			"@media (hover: hover)": {
				default: null,
				":hover": colors.neutralBgDefaultActive,
			},
		},
		transitionDuration: {
			default: null,
			"@media (hover: hover)": {
				default: null,
				":hover": "200ms",
			},
		},
	},
	focusVisible: {
		outline: {
			default: "none",
			":focus-visible": `2px solid ${String(colors.accentBgStrong)}`,
		},
	},
	selected: {
		backgroundColor: {
			default: colors.neutralBgDelicate,
			"@media (hover: hover)": {
				default: colors.neutralBgDelicate,
				":hover": colors.neutralBgWeak,
			},
		},
	},
	disabled: {
		cursor: "default",
		backgroundColor: {
			default: colors.neutralBgDefault,
			":hover": colors.neutralBgDefault,
			":focus-visible": colors.neutralBgDefault,
		},
	},
});

export const WrapperClickable = ({
	render,
	disabled,
	children,
	loading,
	selected,
	sx,
	...props
}: WrapperClickableProps) => {
	const rendered = useRender({
		render,
		defaultTagName: "button",
		props: mergeProps<"button">(props, {
			...stylex.props(styles.root, sx),
			disabled: disabled || undefined,
			children: (
				<Wrapper
					render={<span />}
					sx={[
						styles.wrapper,
						styles.focusVisible,
						!selected && !(disabled || loading) && styles.wrapperHover,
						selected && styles.selected,
						(disabled || loading) && styles.disabled,
					]}
				>
					{children}
				</Wrapper>
			),
		}),
	});

	return rendered;
};
